import { z } from "zod";

export enum UserWorkspaceRole {
    PRIMARY_ADMIN = "primary_admin",
    ADMIN = "admin",
    ACCOUNTANT = "accountant",
    MEMBER = "member",
    VIEW_ONLY = "view_only",
}

/**
 * Shown to a View-only user whenever a change is refused, and deliberately the
 * same sentence in both places it can surface: the API's 403 body
 * (`ViewOnlyAccessService`) and the frontend's click-time alert. A control that
 * refuses on click and an endpoint that refuses on call should not phrase it
 * differently.
 */
export const VIEW_ONLY_WRITE_REFUSAL_MESSAGE =
    "Your role is view-only. You cannot make changes in this workspace.";

/**
 * Privilege order, highest first. `validateManageWorkspaceMemberRole`
 * (`helpers/workspacePermissions.ts`) slices from the acting user's own index,
 * so a role's position decides who may assign it. VIEW_ONLY is last
 * deliberately: that is what makes it assignable by every role, including
 * MEMBER.
 */
export const userWorkspaceRolesInOrder = [
    UserWorkspaceRole.PRIMARY_ADMIN,
    UserWorkspaceRole.ADMIN,
    UserWorkspaceRole.ACCOUNTANT,
    UserWorkspaceRole.MEMBER,
    UserWorkspaceRole.VIEW_ONLY,
] as const;

/**
 * The roles the invite and change-role endpoints accept, in picker display
 * order. Dropping a role from this list is the kill switch: it closes the API,
 * the members-UI picker and pending-invite acceptance together.
 *
 * Enumerated deliberately, NOT spread from `userWorkspaceRolesInOrder` — that
 * array begins with PRIMARY_ADMIN, and a spread would make workspace ownership
 * assignable through the invite contract and the role dropdown. Ownership moves
 * only through `transferPrimaryAdmin`, which has its own rules.
 */
export const ASSIGNABLE_WORKSPACE_ROLES: readonly UserWorkspaceRole[] = [
    UserWorkspaceRole.ADMIN,
    UserWorkspaceRole.MEMBER,
    UserWorkspaceRole.ACCOUNTANT,
    UserWorkspaceRole.VIEW_ONLY,
];

export function isAssignableWorkspaceRole(role: UserWorkspaceRole): boolean {
    return ASSIGNABLE_WORKSPACE_ROLES.includes(role);
}

export const assignableWorkspaceRoleSchema = z
    .nativeEnum(UserWorkspaceRole)
    .refine(isAssignableWorkspaceRole, {
        message: "Role cannot be assigned",
    });

export enum WorkspaceAction {
    LOCKS_EDIT = "locks_edit",
    COA_EDIT = "coa_edit",
    CUSTOM_CATEGORIES_EDIT = "custom_categories_edit",
    ENTITY_SETTINGS_EDIT = "entity_settings_edit",
    ENTITY_CREATE = "entity_create",
    PAYROLL_VIEW = "payroll_view",
    MEMBERS_VIEW = "members_view",
    MEMBERS_EDIT = "members_edit",
    BILLING_EDIT = "billing_edit",
    WORKSPACE_SETTINGS_EDIT = "workspace_settings_edit",
}

const VIEW_ONLY_ACTIONS: readonly WorkspaceAction[] = [
    WorkspaceAction.MEMBERS_VIEW,
];

const MEMBER_ACTIONS: readonly WorkspaceAction[] = [...VIEW_ONLY_ACTIONS];

const ACCOUNTANT_ACTIONS: readonly WorkspaceAction[] = [
    ...MEMBER_ACTIONS,
    WorkspaceAction.LOCKS_EDIT,
    WorkspaceAction.COA_EDIT,
    WorkspaceAction.CUSTOM_CATEGORIES_EDIT,
    WorkspaceAction.ENTITY_SETTINGS_EDIT,
    WorkspaceAction.ENTITY_CREATE,
    WorkspaceAction.PAYROLL_VIEW,
    WorkspaceAction.MEMBERS_EDIT,
    WorkspaceAction.WORKSPACE_SETTINGS_EDIT,
];

const ADMIN_ACTIONS: readonly WorkspaceAction[] = [...ACCOUNTANT_ACTIONS];

const PRIMARY_ADMIN_ACTIONS: readonly WorkspaceAction[] = [
    ...ADMIN_ACTIONS,
    WorkspaceAction.BILLING_EDIT,
];

export const USER_WORKSPACE_ROLE_ACTIONS: Record<
    UserWorkspaceRole,
    readonly WorkspaceAction[]
> = {
    [UserWorkspaceRole.PRIMARY_ADMIN]: PRIMARY_ADMIN_ACTIONS,
    [UserWorkspaceRole.ADMIN]: ADMIN_ACTIONS,
    [UserWorkspaceRole.ACCOUNTANT]: ACCOUNTANT_ACTIONS,
    [UserWorkspaceRole.MEMBER]: MEMBER_ACTIONS,
    [UserWorkspaceRole.VIEW_ONLY]: VIEW_ONLY_ACTIONS,
};
